import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { register, clearError } from '../store/authSlice';
import Alert from '../components/Alert';
import '../styles/Auth.css';

function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(clearError());
    setFormError('');
    setSuccessMessage('');
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    
    if (!fullName.trim()) {
      setFormError('Full name is required');
      return;
    }
    
    if (!email.trim()) {
      setFormError('Email address is required');
      return;
    }
    
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }
    
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    console.log('📝 Submitting registration for:', email);
    
    try {
      const result = await dispatch(register({ email, password, fullName }));
      console.log('📝 Registration result:', result);
      
      if (result.type === 'auth/register/fulfilled') {
        console.log('✅ Registration successful!');
        setFormError('');
        setSuccessMessage('Account created successfully! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        console.error('❌ Registration failed:', result);
        const errorMsg = result.payload || result.error?.message || 'Registration failed. Please check your information and try again.';
        setSuccessMessage('');
        setFormError(errorMsg);
      }
    } catch (err) {
      console.error('❌ Registration exception:', err);
      setSuccessMessage('');
      setFormError('Network error: Unable to connect to server. Please check your connection and try again.');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>🏭 IndustrialIQ</h1>
          <p>AI-Powered Document Intelligence</p>
        </div>
        
        <form onSubmit={handleSubmit} className="auth-form">
          <h2>Create Your Account</h2>
          
          {successMessage && (
            <Alert 
              type="success" 
              message={successMessage}
              onClose={() => setSuccessMessage('')}
            />
          )}
          
          {(formError || error) && (
            <Alert 
              type="error" 
              message={formError || error}
              onClose={() => {
                setFormError('');
                dispatch(clearError());
              }}
            />
          )}
          
          <div className="form-group">
            <label htmlFor="fullName">Full Name</label>
            <input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Doe"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              required
            />
          </div>
          
          <button type="submit" className="btn-primary" disabled={loading || successMessage}>
            {loading ? 'Creating Account...' : successMessage ? 'Success! Redirecting...' : 'Create Account'}
          </button>
          
          <p className="auth-link">
            Already have an account? <Link to="/login">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default Register;
